// Pure progression model — completion, best times, unlock gating. Storage-agnostic
// (the platform layer persists it); fully unit-testable.
import { mergeEconomy, type EconomyState } from './economy';

export interface LevelResult {
  completed: boolean;
  bestTimeMs: number | null;
  bestDeaths: number | null;
}

export interface ProgressData {
  levels: Record<string, LevelResult>;
  /** Currency + purchased upgrades (Phase 11); rides along in the saved/synced blob. */
  economy?: EconomyState;
}

export function emptyProgress(): ProgressData {
  return { levels: {} };
}

export function getResult(data: ProgressData, levelId: string): LevelResult {
  return data.levels[levelId] ?? { completed: false, bestTimeMs: null, bestDeaths: null };
}

/** Record a win, keeping the best (lowest) time and death count. Returns new data. */
export function recordCompletion(
  data: ProgressData,
  levelId: string,
  timeMs: number,
  deaths: number,
): ProgressData {
  const prev = getResult(data, levelId);
  const bestTimeMs = prev.bestTimeMs === null ? timeMs : Math.min(prev.bestTimeMs, timeMs);
  const bestDeaths = prev.bestDeaths === null ? deaths : Math.min(prev.bestDeaths, deaths);
  // Spread `data` so economy (wallet + collected) survives — dropping it here reset
  // currency and the collected set on every level completion.
  return {
    ...data,
    levels: { ...data.levels, [levelId]: { completed: true, bestTimeMs, bestDeaths } },
  };
}

/** A level is unlocked if it's first, or the previous one in order is completed. */
export function isUnlocked(data: ProgressData, orderedIds: string[], levelId: string): boolean {
  const idx = orderedIds.indexOf(levelId);
  if (idx <= 0) return true;
  return getResult(data, orderedIds[idx - 1]).completed;
}

function minNullable(a: number | null, b: number | null): number | null {
  if (a === null) return b;
  if (b === null) return a;
  return Math.min(a, b);
}

/** Combine two progress sets (e.g. local + remote): a level is completed if either has
 *  it, keeping the best time/deaths. Order-independent. */
export function mergeProgress(a: ProgressData, b: ProgressData): ProgressData {
  const ids = new Set([...Object.keys(a.levels), ...Object.keys(b.levels)]);
  const levels: Record<string, LevelResult> = {};
  for (const id of ids) {
    const ra = getResult(a, id);
    const rb = getResult(b, id);
    levels[id] = {
      completed: ra.completed || rb.completed,
      bestTimeMs: minNullable(ra.bestTimeMs, rb.bestTimeMs),
      bestDeaths: minNullable(ra.bestDeaths, rb.bestDeaths),
    };
  }
  const merged: ProgressData = { levels };
  if (a.economy || b.economy) {
    merged.economy = mergeEconomy(a.economy ?? emptyEco(), b.economy ?? emptyEco());
  }
  return merged;
}

function emptyEco(): EconomyState {
  return { wallet: { coins: 0, diamonds: 0 }, upgrades: {}, collected: [] };
}

/** Fraction of levels completed, 0..1. */
export function completionRatio(data: ProgressData, orderedIds: string[]): number {
  if (orderedIds.length === 0) return 0;
  const done = orderedIds.filter((id) => getResult(data, id).completed).length;
  return done / orderedIds.length;
}
